import type {
  InHousePackage,
  InHousePackagePattern,
  SelfReportConfirmationQuestionColumn,
  SelfReportConfirmationRow,
  SelfReportInstanceRow,
  SelfReportPatternConfig,
  SelfReportQuestionColumn,
} from '../-query/type';

/**
 * テナント専用パッケージのモックデータ。
 *
 * NOTE: バックエンド並行開発中のためのローカルフィクスチャ。
 * 実 API 確定後は `-query/api.ts` の取得関数ごと差し替える。
 */

/** サイドバーに並ぶパッケージ（セルフレポート / 申請） */
export const IN_HOUSE_PACKAGES: InHousePackage[] = [
  {
    id: 'self-report',
    type: 'SelfReport',
    labelKey: 'selfReport',
    instanceCount: 2,
  },
  {
    id: 'application',
    type: 'Application',
    labelKey: 'application',
    instanceCount: 2,
  },
];

/** セルフレポートのパターン（デザイン: PC入替前アンケート） */
export const SELF_REPORT_PATTERNS: InHousePackagePattern[] = [
  {
    operationPackagePatternId: 'pat-pc-replacement-survey',
    name: 'PC入替前アンケート',
  },
];

/** セルフレポート一覧の Form 設問列 */
export const SELF_REPORT_QUESTION_COLUMNS: SelfReportQuestionColumn[] = [
  { questionDefinitionId: 'q-device-usage', name: '現在の PC 利用状況' },
  { questionDefinitionId: 'q-replacement-timing', name: '入替希望時期' },
];

const target = (targetId: string, targetName: string) => ({
  targetType: 'person',
  targetId,
  targetName,
});

/**
 * セルフレポート一覧（行政申請と同じグルーピング表示）。
 * - グループ 1: 基準日 2026-05-01（タグなし）
 * - グループ 2: タグ「tag name」/ 基準日 2026-06-02
 */
export const SELF_REPORT_INSTANCES: SelfReportInstanceRow[] = [
  {
    id: 'inst-1',
    completedOperationStepCount: 2,
    totalOperationStepCount: 2,
    target: target('p-1', 'Taro Tanaka'),
    anchorDate: '2026-05-01',
    tag: null,
    columns: [
      { questionDefinitionId: 'q-device-usage', answerStatus: 'Completed' },
      {
        questionDefinitionId: 'q-replacement-timing',
        answerStatus: 'Completed',
      },
    ],
  },
  {
    id: 'inst-2',
    completedOperationStepCount: 1,
    totalOperationStepCount: 2,
    target: target('p-2', 'Taro Tanaka'),
    anchorDate: '2026-05-01',
    tag: null,
    columns: [
      { questionDefinitionId: 'q-device-usage', answerStatus: 'Completed' },
      {
        questionDefinitionId: 'q-replacement-timing',
        answerStatus: 'InProgress',
      },
    ],
  },
  {
    id: 'inst-3',
    completedOperationStepCount: 0,
    totalOperationStepCount: 2,
    target: target('p-3', 'Taro Tanaka'),
    anchorDate: '2026-06-02',
    tag: { id: 'tag-1', name: 'tag name' },
    columns: [
      { questionDefinitionId: 'q-device-usage', answerStatus: 'InProgress' },
      {
        questionDefinitionId: 'q-replacement-timing',
        answerStatus: 'NotStarted',
      },
    ],
  },
  {
    id: 'inst-4',
    completedOperationStepCount: 0,
    totalOperationStepCount: 2,
    target: target('p-4', 'Taro Tanaka'),
    anchorDate: '2026-06-02',
    tag: { id: 'tag-1', name: 'tag name' },
    columns: [
      { questionDefinitionId: 'q-device-usage', answerStatus: 'NotStarted' },
      {
        questionDefinitionId: 'q-replacement-timing',
        answerStatus: 'NotStarted',
      },
    ],
  },
];

/**
 * セルフレポート確認画面の Form 設問列（デザイン: PC入替前アンケート）。
 * 列見出しは Form の設問（キー）がそのまま並ぶ想定。
 */
export const SELF_REPORT_CONFIRMATION_QUESTION_COLUMNS: SelfReportConfirmationQuestionColumn[] =
  [
    { questionDefinitionId: 'q-pc-disposition', name: '1:PC処遇' },
    { questionDefinitionId: 'q-work-software', name: '2:業務・ソフト' },
    { questionDefinitionId: 'q-monitor', name: '3:モニター' },
  ];

/**
 * セルフレポート確認画面の対象者別回答（固定モック）。
 * 対象者 / 部門 / ステータス / 回答日時は固定。設問の回答値は `answers` に格納する。
 */
export const SELF_REPORT_CONFIRMATION_ROWS: SelfReportConfirmationRow[] = [
  {
    instanceId: 'inst-sr-1',
    person: { id: 'p-1', name: '山田太郎' },
    department: '営業部',
    status: 'InProgress',
    answeredAt: '2026-04-24T13:56:00+09:00',
    answers: {
      'q-pc-disposition': '-',
      'q-work-software': '-',
      'q-monitor': '不要',
    },
  },
  {
    instanceId: 'inst-sr-2',
    person: { id: 'p-2', name: '佐藤花子' },
    department: 'マーケティング部',
    status: 'InProgress',
    answeredAt: '2026-04-24T13:56:00+09:00',
    answers: {
      'q-pc-disposition': '-',
      'q-work-software': 'Figma,Adobe Photoshop',
      'q-monitor': '1',
    },
  },
  {
    instanceId: 'inst-sr-3',
    person: { id: 'p-3', name: '鈴木一郎' },
    department: 'エンジニア部',
    status: 'Completed',
    answeredAt: '2026-04-24T13:56:00+09:00',
    answers: {
      'q-pc-disposition': '入れ替え',
      'q-work-software': 'Figma,Adobe Photoshop',
      'q-monitor': '2',
    },
  },
  {
    instanceId: 'inst-sr-4',
    person: { id: 'p-4', name: '高橋良子' },
    department: '人事部',
    status: 'InProgress',
    answeredAt: '2026-04-24T13:56:00+09:00',
    answers: {
      'q-pc-disposition': '入れ替え',
      'q-work-software': '-',
      'q-monitor': '不要',
    },
  },
  {
    instanceId: 'inst-sr-5',
    person: { id: 'p-5', name: '田中健二' },
    department: '経理部',
    status: 'Completed',
    answeredAt: '2026-04-24T13:56:00+09:00',
    answers: {
      'q-pc-disposition': '返却',
      'q-work-software': 'Figma,Adobe Photoshop',
      'q-monitor': '1',
    },
  },
  {
    instanceId: 'inst-sr-6',
    person: { id: 'p-6', name: '阿部奈美' },
    department: 'デザイン部',
    status: 'InProgress',
    answeredAt: '2026-04-24T13:56:00+09:00',
    answers: {
      'q-pc-disposition': '入れ替え',
      'q-work-software': 'Figma,Adobe Photoshop',
      'q-monitor': '1',
    },
  },
  {
    instanceId: 'inst-sr-7',
    person: { id: 'p-7', name: '野口恭佑' },
    department: '営業部',
    status: 'Completed',
    answeredAt: '2026-04-24T13:56:00+09:00',
    answers: {
      'q-pc-disposition': '返却',
      'q-work-software': '-',
      'q-monitor': '3',
    },
  },
];

/** 申請パッケージ用モックインスタンス（MailAddressCreation 表示型） */
export const APPLICATION_INSTANCES = [
  {
    id: 'app-inst-1',
    target: target('p-5', 'Hanako Sato'),
    anchorDate: '2026-06-01',
    progress: { completed: 1, total: 3 },
  },
  {
    id: 'app-inst-2',
    target: target('p-6', 'Jiro Suzuki'),
    anchorDate: '2026-06-03',
    progress: { completed: 0, total: 3 },
  },
];

/** 設問エディタのパッケージ名（固定表示。デザイン: 標準外業務パッケージ） */
export const SELF_REPORT_PACKAGE_NAME = '標準外業務パッケージ';

/** 設問エディタのメタ表示に使う担当者（固定モック） */
const MOCK_PATTERN_EDITOR = { id: 'p-1', name: 'Taro Tanaka' };

/**
 * セルフレポートのパターン設定（編集モードの初期値）。
 * `SELF_REPORT_PATTERNS` と同じ operationPackagePatternId を使う。
 * 設問は確認画面の列（PC処遇 / 業務・ソフト / モニター）に対応した 3 問。
 */
export const SELF_REPORT_PATTERN_CONFIGS: SelfReportPatternConfig[] = [
  {
    operationPackagePatternId: 'pat-pc-replacement-survey',
    packageName: SELF_REPORT_PACKAGE_NAME,
    name: 'PC入替前アンケート',
    description: '',
    dueDate: null,
    status: 'draft',
    createdBy: MOCK_PATTERN_EDITOR,
    updatedBy: MOCK_PATTERN_EDITOR,
    updatedAt: '2026/05/17 14:23',
    questions: [
      {
        id: 'q-pc-disposition',
        type: 'radio',
        title: '入替対象PCの処遇',
        description: '',
        required: true,
        options: [
          { id: 'opt-pc-1', label: '入れ替え' },
          { id: 'opt-pc-2', label: '返却' },
          { id: 'opt-pc-other', label: '', isOther: true },
        ],
      },
      {
        id: 'q-work-software',
        type: 'checkbox',
        title: '業務で使用するソフトウェア',
        description: '',
        required: true,
        options: [
          { id: 'opt-sw-1', label: 'Figma' },
          { id: 'opt-sw-2', label: 'Adobe Photoshop' },
        ],
      },
      {
        id: 'q-monitor',
        type: 'text',
        title: 'モニターの希望があれば記入してください',
        description: '',
        required: false,
        options: [],
      },
    ],
  },
];
